/**
 * Wire types. These mirror the API's public dicts exactly, snake_case
 * included: one shape on the wire, in the client and in the screens, so a
 * renamed field breaks the typecheck rather than a screen at runtime.
 */

export type Severity = 'Informational' | 'Low' | 'Medium' | 'High' | 'Critical';
/** A source that is polled and registered per Client. */
export type SourceName = 'defender' | 'sentinelone';
/**
 * A detection's source: a polled provider, or `bluesphere`, the platform's
 * own rule engine (ADR-0020), which is registered on no Client.
 */
export type DetectionSourceName = SourceName | 'bluesphere';
/**
 * Everything in the connector registry, which is wider than `SourceName`:
 * Entra declares user response actions (task 82) but is polled nowhere and
 * registered on no Client — it rides the Defender admin consent. Only the
 * Integrations catalog is this wide; registration stays `SourceName`.
 */
export type IntegrationName = SourceName | 'entra' | EventSourceName | PlatformIntegrationName;
/**
 * A platform-credential integration (task 161, requirements r17): one vendor
 * credential for the platform, and each Client *mapped* to one vendor
 * organization (NinjaOne) or company (ConnectSecure).
 */
export type PlatformIntegrationName = 'ninjaone' | 'connectsecure';
/**
 * An event source (Story 24, task 157): registered per Client like a
 * `SourceName`, but it produces events, not detections, so a detection's
 * `source` is never one of these. `generic` is the generic JSON ingress
 * (task 129): pushed to `POST /ingest/{source_id}`, never polled.
 */
export type EventSourceName = 'entra_logs' | 'generic' | 'latticeai_dlp';
export type EventFeed =
  | 'entra_signin'
  | 'entra_signin_noninteractive'
  | 'entra_audit'
  | 'm365_audit'
  | 'generic'
  | 'latticeai_dlp'
  | 'cloudtrail';
/** `unlicensed`: the provider refuses the feed without a license (Entra ID P1 for sign-ins). */
export type FeedState = 'ok' | 'unlicensed' | 'error';
export type AlertStatus = 'open' | 'triaged' | 'in_progress' | 'resolved' | 'false_positive';
/** Kept for call sites written before the Alert layer; status is the alert's. */
export type DetectionStatus = AlertStatus;
export type Disposition = 'suspicious' | 'actionable';
export type Outcome =
  | 'raise_actionable'
  | 'raise_suspicious'
  | 'watch'
  | 'resolve_as_indicator'
  | 'resolve'
  | 'skip';
export type ProviderStatus = 'open' | 'closed';
export type SourceHealthStatus = 'healthy' | 'degraded' | 'pending';
export type ConnectorRole =
  | 'detection'
  | 'log'
  | 'identity'
  | 'endpoint'
  | 'enrichment'
  | 'notification'
  | 'ticketing';
export type ConnectorMaturity = 'ga' | 'beta';

/**
 * What a connector declares it could do (Story 18, ADR-0013): actions by
 * asset type, the action that releases each one, and maturity. Declaration
 * only — nothing in the MVP executes an action. `role` also selects the
 * source's health threshold.
 */
export interface CapabilityManifest {
  role: ConnectorRole;
  actions: Record<string, string[]>;
  releases: Record<string, string>;
  maturity: ConnectorMaturity;
}

/** Story 8's table: the only legal moves from each status. */
export const TRANSITIONS: Record<AlertStatus, AlertStatus[]> = {
  open: ['triaged', 'false_positive'],
  triaged: ['in_progress', 'false_positive'],
  in_progress: ['resolved', 'false_positive'],
  resolved: ['open'],
  false_positive: ['open'],
};

// ---------- Clients ----------

/** `active`: in service with no Defender consent needed, a Client engaged
 * without MDR (design r17, task 224). */
export type ClientTenantStatus = 'pending' | 'consented' | 'active' | 'revoked';

/** A source's registration on the Client, plus its stored health when the caller may read it. */
export interface SourceInfo {
  state: 'enabled' | 'removed';
  connected_at: string | null;
  removed_at?: string | null;
  health?: SourceHealthStatus;
  last_success_at?: string | null;
  last_error?: string | null;
  last_error_at?: string | null;
  consecutive_failures?: number;
  watermark?: string | null;
  backfilling?: boolean;
  detections_24h?: number;
  as_of?: string;
  /** Declared by the connector; null for a source the platform does not know. */
  capabilities?: CapabilityManifest | null;
  /** `present` once a write-scoped response credential is stored (Story 19). */
  response_token?: 'present' | null;
  /** Event sources only (task 157): each feed's state and how far it has read. */
  feeds?: Partial<Record<EventFeed, FeedHealth>>;
  /**
   * Mapped sources only (task 161): `{organization_id, organization_name}` for
   * NinjaOne, `{company_id, company_name}` for ConnectSecure. Never a credential.
   */
  config?: Record<string, string>;
}

/** A Client, or BlueSphere's own platform-operations tenant (task 183). */
export type ClientTenantKind = 'client' | 'operations';

export interface FeedHealth {
  /** null until the source's first poll has run. */
  state: FeedState | null;
  watermark: string | null;
  unlicensed_since: string | null;
  last_error: string | null;
}

export interface ClientTenant {
  client_tenant_id: string;
  display_name: string;
  status: ClientTenantStatus;
  /**
   * `operations` only on the platform-operations tenant (task 183), which
   * `listTenants(…, { includeOperations: true })` adds for scope `all`.
   */
  kind: ClientTenantKind;
  invite_created_at: string;
  /** A pool `sub`, on a Client invited before task 217. */
  created_by_sub: string | null;
  /** MDR's user id of whoever invited the Client (task 217). */
  created_by_user_id: string | null;
  entra_tenant_id: string | null;
  consented_at: string | null;
  consented_scopes: string[] | null;
  revoked_at: string | null;
  external_client_id?: string | null;
  sources: Record<string, SourceInfo>;
  /** Response settings (Story 19); the defaults — off, test — when never set. */
  response: ResponseSettings;
  /** Story 35 (task 138): off, with defaults, until an Admin enables it. */
  outreach: OutreachSettings;
  /**
   * Permissions the app declares that this Client's consent predates (task 80).
   * Non-empty: offer "Update permissions" (`reconsentTenant`); the response
   * rails withhold the actions that need them until it is used.
   */
  missing_scopes: string[];
  /**
   * Billable users (Story 21): the daily Usage Unit count — a licensed,
   * enabled account in the Client's identity provider. All null until the
   * first count. On a failed count the previous number stands and
   * `billable_state` says why.
   */
  billable_users: number | null;
  billable_breakdown: Record<string, number> | null;
  billable_counted_at: string | null;
  billable_state: BillableState | null;
  billable_error: string | null;
  /**
   * The services the Client is engaged for (Story 45, task 179): keys of the
   * server's registry. A Client from before services existed reads `["mdr"]`.
   */
  services: ServiceKey[];
  /** What those services enable; the response layer refuses anything else. */
  capabilities: string[];
  /** The meter each service bills on this month (Shield checked → the Usage Unit for all). */
  meter: Partial<Record<ServiceKey, ServiceMeter>>;
  /**
   * The DLP meter this month: devices with a heartbeat in it; null without
   * LatticeAI DLP. On `GET /tenants` only; other routes returning a Client
   * leave it out (task 179c).
   */
  protected_devices?: { month: string; count: number } | null;
}

/** A service the platform offers (Story 45). */
export type ServiceKey = 'mdr' | 'latticeai_dlp';
export type ServiceMeter = 'usage_unit' | 'protected_devices';

/** Why there is, or is not, a fresh Usage Unit count (Story 21). */
export type BillableState = 'ok' | 'no_idp' | 'missing_permission' | 'error';

/** `POST /tenants/{t}/reconsent`: shown once, like the create-time consent URL. */
export interface ReconsentResult {
  consent_url: string;
  missing_scopes: string[];
}

export interface SentinelOneResponseToken {
  api_token: string;
}

export interface CreateTenantResult extends ClientTenant {
  /** Absent for a Client created without MDR: it is `active` at once, with no
   * Defender consent to ask for (design r17, task 224). */
  consent_url?: string;
}

export interface ListTenantsResult {
  tenants: ClientTenant[];
}

export interface SentinelOneCredentials {
  console_url: string;
  api_token: string;
}

// ---------- Integrations (task 69) ----------

/** One entry of the connector registry: what the platform can connect to. */
export interface IntegrationSummary {
  name: IntegrationName;
  manifest: CapabilityManifest;
}

export interface ListIntegrationsResult {
  integrations: IntegrationSummary[];
}

/** A platform integration's credential state (task 161). The credential itself is never returned. */
export interface PlatformIntegrationStatus {
  name: PlatformIntegrationName;
  connected: boolean;
  credential_changed_at: string | null;
  /** Clients with an enabled mapping; the credential cannot be removed while this is above 0. */
  mapped_clients: number;
}

export interface NinjaOneCredentialInput {
  /** One of NinjaOne's regional instances: `app` (North America), `eu`, `oc`. */
  region: 'app' | 'eu' | 'oc';
  client_id: string;
  client_secret: string;
}

export interface ConnectSecureCredentialInput {
  /** The pod host, e.g. `pod104.myconnectsecure.com`. */
  pod: string;
  tenant: string;
  client_id: string;
  client_secret: string;
}

/** A NinjaOne organization or ConnectSecure company, and the Client it is mapped to. */
export interface VendorAccount {
  id: string;
  name: string;
  mapped_to: { tenant_id: string; name: string } | null;
}

export interface ListVendorAccountsResult {
  items: VendorAccount[];
}

// ---------- Detections ----------

/** How a DLP detection was joined to the EDR's asset for the same machine (task 184). */
export interface AssetJoin {
  /** Today always `hostname`; an EDR device id once the DLP side sends one. */
  by: 'hostname' | string;
  /** The EDR that reported the asset, e.g. `defender`. */
  source: string;
  natural_key: string;
  /** The EDR's name for the machine. */
  display_name: string;
  /** How many EDR assets answered to the name; the most recently seen won. */
  candidates: number;
}

export interface Detection {
  /** `<source>:<provider_detection_id>`; URL-encode it in a path. */
  detection_id: string;
  source: DetectionSourceName;
  /** The service it belongs to (task 179c); absent on a detection written before. */
  service?: ServiceKey | null;
  provider_detection_id: string;
  title: string;
  severity: Severity;
  score: number;
  first_seen: string;
  last_seen: string;
  provider_updated_at: string;
  provider_status: ProviderStatus;
  provider_determination: string | null;
  incident_id: string | null;
  asset_name: string | null;
  /** Story 15 asset fields; absent on detections written before task 54. */
  device_id?: string | null;
  device_os?: string | null;
  machine_type?: string | null;
  user_principal?: string | null;
  /** System-group slugs of the assets on this detection, e.g. `server`, `nhi`. */
  asset_groups?: string[];
  detection_name: string | null;
  mitre: string[];
  backfill: boolean;
  ai_state: AiState | string;
  ai_summary_short?: string;
  ai_model?: string;
  ai_error?: string;
  ai_failed_at?: string;
  ingestion_time?: string;
  /** What the playbook decided on arrival (Story 16). */
  outcome?: Outcome;
  detection_class?: string;
  playbook_id?: string;
  fired_rule_id?: string;
  /** Operations detections only (task 183): the Client it is about, ids only. */
  about?: { client_tenant_id: string; source_id?: string; device_uid?: string };
  /** Task 184: the endpoint asset a join filed this detection under (a DLP detection, joined to its EDR asset). */
  endpoint_key?: string;
  asset_join?: AssetJoin;
  /** Present only when the detection raised or joined an alert. */
  alert_id?: string;
  short_id?: string;
}

export interface DetectionPage {
  items: Detection[];
  next_cursor: string | null;
}

/** A detection on the all-Clients stream: the row plus whose it is (task 66). */
export interface CrossClientDetection extends Detection {
  client_tenant_id: string;
  display_name: string;
}

export interface CrossClientDetectionPage {
  items: CrossClientDetection[];
  /** Opaque; carries one position per Client. Not interchangeable with a per-Client cursor. */
  next_cursor: string | null;
}

export interface DetectionListParams {
  severity?: string;
  source?: string;
  /** A service key, e.g. `latticeai_dlp` (task 179c). */
  service?: ServiceKey;
  /** A playbook outcome, or the shorthands `raised` / `resolved`. */
  outcome?: string;
  incident?: string;
  /** ISO-8601 lower bound on `first_seen`. */
  since?: string;
  cursor?: string;
  limit?: number;
  /** All-Clients routes: only the Clients under this organization (task 205). */
  org?: string;
}

/** A playbook condition: the JSON tree of `bluesphere_conditions` (task 135). */
export type ConditionNode =
  | { and: ConditionNode[] }
  | { or: ConditionNode[] }
  | { not: ConditionNode }
  | { field: string; op: string; value: string | number | boolean };

export interface TraceEntry {
  rule_id: string;
  name: string;
  condition_text: string;
  result: boolean;
  /** The tree that ran; null for the default rule. Absent on traces from before task 135. */
  condition?: ConditionNode | null;
  /** The registry paths the condition read, with the values it saw. */
  reads?: Record<string, unknown>;
  /** The rule's outcome, whether or not it matched: the replay checks the first-match chain with it. */
  outcome?: Outcome;
  /** Which tier the rule came from (ADR-0017). */
  tier?: 'system' | 'client';
  state?: 'inherited' | 'overridden' | 'client-added';
}

/** The decision trace: every rule considered, in order, and the one that fired. */
export interface DecisionTrace {
  playbook_id: string;
  playbook_version?: number | string;
  detection_class: string;
  entries: TraceEntry[];
  fired_rule_id: string;
  outcome: Outcome;
  acted?: boolean;
  alert_id?: string | null;
  evaluated_at: string;
  /** Where the system tier came from: storage or the shipped code (task 135). */
  system_source?: 'config' | 'code';
  /** The Client delta's version; null when the Client has none. */
  client_version?: number | null;
  /** System rules the Client disabled, so not evaluated. */
  disabled?: string[];
  /** Why the Client's delta was ignored, when it did not resolve. */
  delta_error?: string;
  [key: string]: unknown;
}

export type AiState = 'none' | 'pending' | 'ready' | 'failed';

/** The AI panel: the detection's `ai_state` plus the `#AI` item when it exists. */
export interface AiPanel {
  state: AiState | string;
  summary?: string;
  suspected_goal?: string;
  investigation_steps?: string[];
  remediation?: string[];
  model?: string;
  generated_at?: string;
  input_tokens?: number;
  output_tokens?: number;
  quality_degraded?: boolean;
  prompt_version?: string;
  /** Present when state is `failed`: cap_hit:tenant, content_policy, bad_output, bedrock_error:… */
  failed_reason?: string;
  [key: string]: unknown;
}

export interface EnrichmentResponse {
  state: AiState | string;
  /** true when this call is the one that invoked the enrich Lambda. */
  requested: boolean;
}

/** What a detection carries about its alert: enough to link and label. */
export interface AlertSummary {
  alert_id: string;
  short_id: string;
  status: AlertStatus;
  disposition: Disposition;
  severity: Severity;
  score: number;
  member_count: number;
}

export interface DetectionDetail {
  detection: Detection;
  ai: AiPanel;
  trace: DecisionTrace | null;
  alert: AlertSummary | null;
}

/**
 * The provider's full payload for one detection version, read from the
 * lake on demand (task 67). Never part of the list or the detail.
 */
export interface DetectionPayload {
  detection_id: string;
  source: DetectionSourceName;
  provider_detection_id: string;
  provider_updated_at: string | null;
  retrieved_at: string;
  /** The parsed object; null only when the stored body was not JSON. */
  payload: unknown;
  /** The verbatim body when it could not be parsed as JSON. */
  payload_text: string | null;
}

// ---------- Alerts ----------

/** TTD / TTT / TTC in seconds, derived on read; null while undefined. */
export interface Timing {
  ttd_seconds: number | null;
  ttt_seconds: number | null;
  ttc_seconds: number | null;
}

export interface Alert {
  alert_id: string;
  /** `BSM-<n>`, per Client. */
  short_id: string;
  status: AlertStatus;
  lifecycle: 'ACTIVE' | 'CLOSED';
  disposition: Disposition;
  score: number;
  severity: Severity;
  title: string;
  sources: string[];
  /** The services its members belong to (task 179c); absent on an alert written before. */
  services?: ServiceKey[];
  asset_names: string[];
  /** System-group slugs merged across members (Story 15); absent on alerts raised before task 54. */
  groups?: string[];
  incident_id: string | null;
  incident_source?: string | null;
  member_count: number;
  detected_at: string;
  first_written_at: string;
  triaged_at: string | null;
  remediated_at: string | null;
  closed_at?: string | null;
  reopen_count: number;
  related_alert_ids: string[];
  resolution_source?: string;
  assignee?: string | null;
  tags: string[];
  created_at: string;
  updated_at: string;
  backfill: boolean;
  timing: Timing;
}

export interface AlertPage {
  items: Alert[];
  next_cursor: string | null;
}

/** An alert on the all-Clients queue: the row plus whose it is (task 66). */
export interface CrossClientAlert extends Alert {
  client_tenant_id: string;
  display_name: string;
}

export interface CrossClientAlertPage {
  items: CrossClientAlert[];
  /** Opaque; carries one position per Client. Not interchangeable with a per-Client cursor. */
  next_cursor: string | null;
}

export interface AlertListParams {
  /** A status, `active` / `closed` for either half, or `all` for both (task 90). */
  status?: string;
  severity?: string;
  source?: string;
  /** A service key, e.g. `latticeai_dlp` (task 179c). */
  service?: ServiceKey;
  disposition?: string;
  /** ISO-8601 lower bound on `detected_at` (task 89). */
  since?: string;
  /**
   * ISO-8601 strict lower bound on `first_written_at` — the raise, not the
   * provider's detection time — excluding back-fill. The beacon's poll
   * (Story 25, task 106).
   */
  raised_since?: string;
  cursor?: string;
  limit?: number;
  /** All-Clients routes: only the Clients under this organization (task 205). */
  org?: string;
}

export interface AlertMember {
  detection: Detection;
  trace: DecisionTrace | null;
  ai: AiPanel;
}

/** One entry on the alert's timeline: raise, join, reopen, transition, provider closure. */
export interface AlertEvent {
  event_id: string;
  event: string;
  actor: string;
  actor_email?: string;
  from_status?: string;
  to_status?: string;
  note?: string;
  provider_determination?: string | null;
  detection_id?: string;
  rule_id?: string;
  at: string;
  [key: string]: unknown;
}

/** Kept for call sites written before the Alert layer. */
export type TriageEvent = AlertEvent;

export type AiTitleState = 'none' | 'pending' | 'ready' | 'stale' | 'failed';

/**
 * The advisory title of a multi-member alert (task 77b): drafted by the
 * model over every member, stored beside the summaries, display-only and
 * never matchable in a rule (ADR-0011). `stale` = ready, but over fewer
 * members than the alert now has.
 */
export interface AiTitle {
  state: AiTitleState | string;
  member_count: number;
  title?: string;
  model?: string;
  generated_at?: string;
  prompt_version?: string;
  quality_degraded?: boolean;
  member_count_at?: number;
  failed_reason?: string;
  failed_at?: string;
  claimed_at?: string;
  [key: string]: unknown;
}

export interface AlertTitleResponse {
  state: AiTitleState | string;
  /** true when this call is the one that invoked the Lambda. */
  requested: boolean;
  /** `single_member` when there is nothing to draft. */
  reason?: string;
}

export interface AlertDetail {
  alert: Alert;
  members: AlertMember[];
  events: AlertEvent[];
  timing: Timing;
  ai_title: AiTitle;
  /** Response actions taken or recorded on this alert, newest first (Story 19). */
  actions: ActionRecord[];
  /** Requests a rail stopped, with the rail; equal weight to actions taken. */
  withheld: WithheldEntry[];
  /** What the alert offers now: manifests ∩ settings ∩ target ids ∩ consent, each with its warnings. */
  available_actions: AvailableAction[];
  /** The outreach exchange (Story 35), when the alert's rule asked the user; else null. */
  outreach: AlertOutreach | null;
}

export type OutreachAnswer = 'yes' | 'no' | 'unsure';

/** One person asked. Addresses are masked (`p…@contoso.com`, `…0100`). */
export interface OutreachParty {
  role: 'user' | 'manager' | 'contact';
  principal: string;
  email: string | null;
  phone: string | null;
  answer: OutreachAnswer | null;
  answered_via: string | null;
  answered_at: string | null;
  /** A yes confirmed from the party's phone: a texted YES or a 1 on the call. */
  confirmed: boolean;
  confirm_requested_at: string | null;
  /** `sms` or `voice`: what confirmed the yes; `answered_via` keeps where the yes was given (task 139f). */
  confirmed_via: string | null;
  confirmed_at: string | null;
  /** Each send to this party, read off the outreach timeline, oldest first (task 139f). */
  deliveries: OutreachDelivery[];
}

/** One send: the question, the confirmation, or (to the security contact) the result. */
export interface OutreachDelivery {
  channel: string;
  purpose: 'question' | 'confirm' | 'told';
  ok: boolean;
  /** Why it did not go: `not_configured`, `not_in_conversation`, a provider error code. */
  error: string | null;
  at: string | null;
}

export interface AlertOutreach {
  state: 'asked' | 'concluded';
  opened_at: string;
  deadline: string;
  question: { what?: string; when?: string };
  rule: { playbook_id?: string; playbook_version?: number; rule_id?: string };
  parties: OutreachParty[];
  conclusion: {
    result: 'no' | 'yes' | 'unsure' | 'timeout' | 'unconfirmed' | 'superseded' | 'skipped';
    outcome: 'actionable' | 'resolve' | 'escalate' | 'none';
    by: string | null;
    at: string;
    reason: string;
  } | null;
}

export interface MetricMean {
  mean_seconds: number | null;
  n: number;
}

export interface AlertMetrics {
  ttd: MetricMean;
  ttt: MetricMean;
  ttc: MetricMean;
  sampled: number;
  truncated: boolean;
}

// ---------- Generic sources (task 129; page task 211) ----------

export type GenericSourceType = 'generic' | 'latticeai_dlp';

/**
 * One source posting to `POST /ingest/{source_id}` with its own key. The key
 * is never part of this shape: it is returned once, on create or reissue.
 */
export interface GenericSource {
  source_id: string;
  name: string;
  state: 'enabled' | 'removed' | string;
  /** `latticeai_dlp` has a built-in mapping (task 180); `generic` carries its own. */
  source_type: GenericSourceType | string;
  key_issued_at: string | null;
  /** The field mapping to OCSF, in task 129's grammar; null for a built-in mapping. */
  mapping: Record<string, unknown> | null;
  mapping_updated_at: string | null;
  created_at: string | null;
  removed_at: string | null;
  requests_per_minute: number;
  events_per_day: number;
  last_received_at: string | null;
  events_received: number;
  batches_received: number;
  mapping_failures: number;
  last_mapping_failure_at: string | null;
  /** The DLP intake guard's counts (task 180); zero for a plain generic source. */
  redactions: number;
  refusals: number;
  last_redaction_at: string | null;
  last_refusal_at: string | null;
}

export interface GenericSourceList {
  sources: GenericSource[];
}

/** Create and reissue: the source and its plaintext key, shown once and never again. */
export interface GenericSourceWithKey {
  source: GenericSource;
  key: string;
}

export interface GenericSourceResult {
  source: GenericSource;
}

/** A name, and either a mapping document or a template (today only `generic-json`). */
export interface CreateGenericSourceBody {
  name: string;
  mapping?: Record<string, unknown>;
  template?: string;
}

// ---------- Assets (Story 15) ----------

export type AssetType = 'endpoint' | 'user';

/** One endpoint or user the detections have named, with the system groups it matched. */
export interface Asset {
  asset_type: AssetType;
  natural_key: string;
  display_name: string;
  device_id?: string | null;
  device_os?: string | null;
  machine_type?: string | null;
  user_principal?: string | null;
  sources: string[];
  /** Effective memberships, recomputed from the group rules on every write. */
  groups: string[];
  /** Manual assignments (Phase 1 automation locks). Empty in the MVP. */
  groups_manual: string[];
  first_seen: string;
  last_seen: string;
  detection_count: number;
  updated_at: string;
}

export interface AssetPage {
  items: Asset[];
  next_cursor: string | null;
}

export interface AssetListParams {
  type?: AssetType;
  cursor?: string;
  limit?: number;
}

// ---------- Asset context (Stories 42-43, task 164) ----------

/** A NinjaOne device whose system or DNS name matches the asset. */
export interface AssetContextDevice {
  uid: string;
  system_name: string | null;
  dns_name: string | null;
  display_name: string | null;
  os: string | null;
  node_class: string | null;
  last_contact: string | null;
  offline: boolean | null;
  serials: string[];
  /** Absent on a device not rewritten since task 164 deployed. */
  console_url: string | null;
}

/** One of the top vulnerabilities by risk. CVSS and EPSS are display strings. */
export interface AssetContextVulnerability {
  cve: string;
  severity: string;
  cvss: string;
  epss: string;
  software: string;
  first_seen: string;
}

/** A ConnectSecure asset whose host name or name matches, with its vulnerability summary. */
export interface AssetContextVulnAsset {
  asset_id: string;
  host_name: string | null;
  name: string | null;
  os: string | null;
  serial: string | null;
  counts: { critical: number; high: number; medium: number; low: number } | null;
  kev_count: number | null;
  max_epss: string | null;
  top: AssetContextVulnerability[];
  last_discovered: string | null;
}

interface AssetContextVendor {
  /** Whether the Client is mapped to this vendor; unmapped vendors are not read. */
  connected: boolean;
  /** When the last complete inventory run finished. */
  last_complete_at: string | null;
}

/**
 * What NinjaOne and ConnectSecure know about one asset, matched by its name
 * without the domain (`name_key`). Display only: nothing decides on it.
 */
export interface AssetContext {
  asset: string;
  name_key: string | null;
  ninjaone: AssetContextVendor & { devices: AssetContextDevice[] };
  connectsecure: AssetContextVendor & { assets: AssetContextVulnAsset[] };
}

// ---------- Overview ----------

export interface CountsByBand {
  critical: number;
  high: number;
  medium: number;
  low: number;
  informational: number;
}

export interface VolumePoint extends CountsByBand {
  /** ISO date, UTC. */
  date: string;
  total: number;
  alerts_raised: number;
}

export interface DetectionToAlert {
  detections: number;
  alerts: number;
  /** detections per alert over the trend window; null when nothing was raised. */
  ratio: number | null;
}

export interface PipelineSources {
  /** Sources registered and not removed. */
  connected: number;
  healthy: number;
}

export interface PipelinePlaybook {
  /** Detections the playbook raised (an alert or a join). */
  raised: number;
  /** Detections the playbook closed without an alert. */
  resolved: number;
  watch: number;
  /**
   * True when at least one day in the window predates the per-outcome
   * counters, so raised/resolved for that day are detections minus alerts.
   */
  derived: boolean;
}

/**
 * Live counts per stage: Sources → Detections → Playbook → Alerts (task 68).
 * The SPA draws Events → Detections → Playbook → Alerts (task 158): the Events
 * stage is intake health from `Overview.sources`, and `sources` here is unused
 * by the strip.
 */
export interface Pipeline {
  window_days: number;
  sources: PipelineSources;
  detections: number;
  playbook: PipelinePlaybook;
  /** Alerts raised in the window (joins to an existing alert do not count). */
  alerts: number;
  /** detections per alert; null when nothing was raised. */
  ratio: number | null;
}

export interface Overview {
  client_tenant_id: string;
  display_name: string;
  /** Open ALERTS by band. */
  counts_by_band: CountsByBand;
  open_total: number;
  volume: VolumePoint[];
  detection_to_alert: DetectionToAlert;
  pipeline: Pipeline;
  sources: Record<string, SourceInfo>;
  attention: Alert[];
}

export interface CrossClientRow {
  client_tenant_id: string;
  display_name: string;
  status: ClientTenantStatus;
  counts_by_band: CountsByBand | null;
  open_total: number;
}

export interface CrossClientResult {
  clients: CrossClientRow[];
}

// ---------- Response actions (Story 19, design §9.7) ----------

export type ResponseMode = 'test' | 'live';

/** The Client's `response` map. Off by default; an enabled Client starts in test mode. */
export interface ResponseSettings {
  endpoint_enabled: boolean;
  user_enabled: boolean;
  mode: ResponseMode;
  daily_cap: number;
  blast_radius: number;
  updated_by: string | null;
  updated_at: string | null;
}

/** `PUT /tenants/{t}/response-settings`: any subset. */
export interface ResponseSettingsUpdate {
  endpoint_enabled?: boolean;
  user_enabled?: boolean;
  mode?: ResponseMode;
  daily_cap?: number;
  blast_radius?: number;
}

// ---------- Outreach (Story 35, task 138) ----------

export type OutreachChecklistItem =
  | 'client_agreement'
  | 'communication_plan'
  | 'training_reconciled'
  | 'sender_identity';

/** Task 139 (deviation 25): voice, WhatsApp, Slack and Teams join email and SMS. */
export type OutreachChannel = 'email' | 'sms' | 'voice' | 'whatsapp' | 'slack' | 'teams';

export interface OutreachSecurityContact {
  name: string;
  email: string | null;
  phone: string | null;
}

/** The Client's Slack workspace (task 139c), set only by the OAuth install; its bot token is never sent. */
export interface OutreachSlackInstall {
  team_id: string;
  team_name: string | null;
  installed_by: string | null;
  installed_at: string | null;
}

/** The Client's `outreach` map. Off until an Admin enables it with every checklist item confirmed. */
export interface OutreachSettings {
  enabled: boolean;
  enabled_by: string | null;
  enabled_at: string | null;
  checklist: Record<OutreachChecklistItem, boolean>;
  window_s: number;
  channels: OutreachChannel[];
  /** Channels that cannot be chosen yet, each with why; choosing one is a 409 `channel_unavailable`. */
  channels_unavailable: Partial<Record<OutreachChannel, string>>;
  max_exchanges_per_day: number;
  security_contact: OutreachSecurityContact | null;
  /** The installed Slack workspace; Slack can be chosen only once there is one. */
  slack: OutreachSlackInstall | null;
  /** What users see as the sender: always "BlueSphere MDR" in the MVP. */
  sender: string;
  updated_by: string | null;
  updated_at: string | null;
}

/** `PUT /tenants/{t}/outreach-settings`: any subset. 409 `checklist_incomplete` / `not_consented` on enabling, `channel_unavailable` on a channel not ready. */
export interface OutreachSettingsUpdate {
  enabled?: boolean;
  checklist?: Partial<Record<OutreachChecklistItem, boolean>>;
  window_s?: number;
  channels?: OutreachChannel[];
  max_exchanges_per_day?: number;
  security_contact?: OutreachSecurityContact | null;
}

export type RailName =
  | 'client_enabled'
  | 'test_mode'
  | 'consent'
  | 'provider_available'
  | 'maturity'
  | 'group_opt_out'
  | 'daily_cap'
  | 'blast_radius'
  // A rule request's own refusals (task 140, design deviation 24).
  | 'unsupported_action'
  | 'no_target'
  | 'action_caution';

export type RailOutcome = 'pass' | 'block' | 'warn' | 'would_block';

export interface RailResult {
  rail: RailName | string;
  outcome: RailOutcome | string;
  detail: string;
}

/** What the rails said, as recorded on the action or the withheld entry. */
export interface RailDecision {
  evaluated: RailResult[];
  blocked_by: string[];
  warnings: string[];
  /** Test mode: rails that would have blocked in live mode. */
  would_block: string[];
  acknowledged: string[];
  test_mode: boolean;
}

/** Who asked: an operator, or a playbook rule (task 140). */
export interface RequesterInfo {
  kind: 'operator' | 'rule';
  /** MDR's user id (task 217). */
  user_id?: string;
  /** A pool `sub`, on an entry written before task 217. */
  sub?: string;
  email?: string;
  playbook_id?: string;
  rule_id?: string;
  /** The playbook version that fired; 0 on an entry written before task 140. */
  version?: number;
}

export interface ActionTarget {
  asset_type: AssetType;
  natural_key: string;
  /** What goes to the provider: the Defender device id, the SentinelOne agent uuid, the UPN. */
  provider_ref: string;
  display_name: string;
  source: string;
}

export type ActionStatus =
  | 'requested'
  | 'running'
  | 'succeeded'
  | 'failed'
  | 'partial'
  | 'test'
  | 'withheld';

export interface ActionRecord {
  alert_id: string;
  action_id: string;
  action: string;
  /** Set on a release: the action it undoes. */
  release_of: string | null;
  /** The action that releases this one, from the manifest; the SPA labels the Release control with it. Null on a release or when none is declared. */
  release: string | null;
  target: ActionTarget;
  provider: string;
  requested_by: RequesterInfo;
  status: ActionStatus | string;
  rails: RailDecision;
  note: string | null;
  requested_at: string;
  started_at: string | null;
  finished_at: string | null;
  operation_id: string | null;
  error: string | null;
  correlation_id?: string;
  [key: string]: unknown;
}

export interface WithheldEntry {
  alert_id: string;
  withheld_id: string;
  action: string;
  release_of: string | null;
  target: ActionTarget;
  provider: string;
  requested_by: RequesterInfo;
  blocked_by: string[];
  rails: RailDecision;
  note: string | null;
  /** Set when the Lambda withheld a request the API had already recorded. */
  action_id?: string;
  /** A rule's request stopped by the cap, the blast radius, an unsupported action or no target: an operator must decide (task 140). */
  escalated?: boolean;
  at: string;
  correlation_id?: string;
  [key: string]: unknown;
}

/** One button: an action on a target, or why there is none. */
export interface AvailableAction {
  action: string;
  release: string | null;
  /** `secondary` marks the cautioned fallback (r20): rendered at lesser weight, after the primaries. */
  emphasis: 'primary' | 'secondary';
  target: ActionTarget;
  provider: string;
  available: boolean;
  /** The blocking rail's detail when `available` is false. */
  reason: string | null;
  /** Rails the operator must acknowledge in the confirmation. */
  warnings: RailResult[];
  test_mode: boolean;
}

export interface ActionRequestBody {
  action: string;
  target: { asset_type: AssetType; natural_key: string };
  note?: string;
  /** Rail names from `AvailableAction.warnings`, acknowledged in the confirmation. */
  acknowledge?: string[];
}

/** 202 with `action` when requested; 200 with `withheld` when a rail stopped it. */
export interface ActionRequestResponse {
  outcome: 'requested' | 'withheld';
  action?: ActionRecord;
  withheld?: WithheldEntry;
}

export interface ActionPage {
  items: ActionRecord[];
  next_cursor: string | null;
}

// ---------- Simulint users (Story 22, ADR-0018) ----------

export type PlatformRole = 'admin' | 'analyst' | 'viewer';
/** `'all'`, or the Client tenant ids the user may address. Never empty when enabled. */
export type UserScope = 'all' | string[];
export type Permission =
  | 'read'
  | 'triage'
  | 'respond'
  | 'manage_client'
  | 'manage_org'
  | 'manage_platform';

/** The organization a user works for (Story 48, ADR-0033): Simulint (the root) or an MSP. */
export interface HomeOrg {
  id: string;
  name: string | null;
  kind: 'simulint' | 'msp';
}

/**
 * `GET /me`: the caller's platform record and, per permission, where they
 * hold it — `'all'`, a list of Client ids, or `false`. The SPA gates on
 * this, never on token claims.
 */
export interface Me {
  user_id: string;
  email: string;
  display_name: string;
  role: PlatformRole;
  scope: UserScope;
  /** Null until the organization tree exists on this environment (task 207). */
  home_org: HomeOrg | null;
  /**
   * Per permission: `'all'`, a list of Client ids, or `false`. `manage_org`
   * is per node instead: the home node's id ("this node and below"), or `false`.
   */
  permissions: Omit<Record<Permission, UserScope | false>, 'manage_org'> & {
    manage_org: string | false;
  };
  /** BlueSphere Identity's user, once this record is bound to one (ADR-0034 §2); otherwise null. */
  directory_user_id: string | null;
}

/** A Simulint user record as the Users page sees it. `bound` is false until first sign-in. */
export interface PlatformUser {
  user_id: string;
  email: string;
  display_name: string;
  role: PlatformRole;
  scope: UserScope;
  enabled: boolean;
  bound: boolean;
  /** The organization the user works for; null until the tree exists (task 207). */
  home_org_id: string | null;
  created_at: string;
  created_by: string;
  updated_at: string;
  updated_by: string;
}

export interface ListUsersResult {
  users: PlatformUser[];
}

/** `POST /users`. `home_org_id` defaults to the caller's home; a list scope stays inside it. */
export interface InviteUserBody {
  email: string;
  display_name?: string;
  role: PlatformRole;
  scope: UserScope;
  home_org_id?: string;
}

/** `PATCH /users/{id}`: any of the four. A new home must be in the caller's subtree. */
export interface UpdateUserBody {
  role?: PlatformRole;
  scope?: UserScope;
  enabled?: boolean;
  home_org_id?: string;
}

/** `GET /users`: the users homed at `org` (default the caller's home), and below unless `children` is false. */
export interface ListUsersParams {
  org?: string;
  children?: boolean;
}

// ---------- MSPs and their Clients (Story 48, ADR-0033, task 205) ----------

export type OrgKind = 'simulint' | 'msp' | 'client' | 'operations';
export type OrgStatus = 'active' | 'suspended' | 'offboarded';

/**
 * One node of the organization tree. Simulint and MSP nodes carry their
 * resale and the cross-MSP switch (`consume_across_msps`: the node's own
 * value, null = inherit; `consumes_across_msps`: what applies). Client and
 * operations nodes carry their name and registry state instead.
 */
export interface OrgNode {
  org_id: string;
  kind: OrgKind;
  parent_id: string | null;
  name: string | null;
  status: OrgStatus;
  created_at: string | null;
  updated_at: string | null;
  resellable?: string[];
  consume_across_msps?: boolean | null;
  consumes_across_msps?: boolean;
  client?: {
    status: string;
    services: string[];
    sources: Record<string, string>;
  } | null;
}

/** `GET /orgs`: the caller's visible subtree, parents before children; empty before the tree exists. */
export interface ListOrgsResult {
  home_org_id: string | null;
  orgs: OrgNode[];
}

/** `POST /orgs`: an MSP and its first Admin. `parent_id` defaults to the caller's home. */
export interface CreateOrgBody {
  name: string;
  parent_id?: string;
  first_admin: { email: string; display_name?: string; role?: 'admin' };
  resellable?: string[];
}

export interface CreateOrgResult {
  org: OrgNode;
  first_admin: PlatformUser;
}

/**
 * `PATCH /orgs/{o}`. A rename needs `manage_org` at the node; status, a move
 * and the cross-MSP switch need it at a strict ancestor (and a move, at the
 * new parent). A Client node only moves. `consume_across_msps: null` reverts
 * to inherited.
 */
export interface UpdateOrgBody {
  name?: string;
  status?: OrgStatus;
  parent_id?: string;
  consume_across_msps?: boolean | null;
}

export interface OrgAuditEntry {
  audit_id: string;
  at: string;
  action: string;
  actor_user_id: string;
  actor_email: string | null;
  target_org_id: string;
  changes: Record<string, { before: unknown; after: unknown }>;
}

export interface OrgAuditPage {
  items: OrgAuditEntry[];
  next_cursor: string | null;
}

/** The settings that resolve through the tree (task 206). */
export type OrgSettingKey = 'email';

/**
 * `GET/PUT/DELETE /orgs/{o}/settings/{key}`: what applies at the node, which
 * node supplied it, and the node's own value when it has one. `version` is
 * the node's own (0 when it inherits) — the one a `PUT` must name.
 */
export interface OrgSettingView {
  org_id: string;
  key: OrgSettingKey;
  effective: Record<string, unknown>;
  source_node_id: string;
  version: number;
  own?: Record<string, unknown>;
  updated_at?: string | null;
  updated_by?: string | null;
}

export interface OrgSettingBody {
  value: Record<string, unknown>;
  expected_version: number;
}

export interface UserAuditEntry {
  audit_id: string;
  at: string;
  action:
    | 'invite'
    | 'update'
    | 'bind'
    | 'entra_oid'
    | 'email_preferences'
    | 'email_settings'
    | 'migrate';
  /** A user id, or a system actor such as `system:binding`. */
  actor_user_id: string;
  actor_email: string;
  target_user_id: string;
  changes: Record<string, { before: unknown; after: unknown }>;
}

export interface UserAuditPage {
  items: UserAuditEntry[];
  next_cursor: string | null;
}

// ---------- Email enrollment and preferences (Story 26, design r18) ----------

export type EmailState = 'on' | 'off';

/**
 * `GET /me/preferences`: the caller's own email state. `per_client`
 * overrides `enrolled` for that Client only. While `locked`, an Admin's
 * role-level setting (`role_email`) is final for the caller and the page
 * is read-only; `PATCH` answers 409 `preferences_locked`.
 */
export interface MyEmailPreferences {
  enrolled: boolean;
  per_client: Record<string, EmailState>;
  role_email: EmailState;
  locked: boolean;
}

/** `PATCH /me/preferences`: `null` drops a Client's override. */
export interface UpdateMyEmailPreferencesBody {
  enrolled?: boolean;
  per_client?: Record<string, EmailState | null>;
}

// ---------- Events (Story 24, task 157) ----------

/** The body of `POST /tenants/{t}/events/search`. Timestamps are ISO 8601 with a zone. */
export interface EventSearchRequest {
  since: string;
  until: string;
  feeds?: EventFeed[];
  classes?: number[];
  user?: string;
  device?: string;
  ip?: string;
  q?: string;
}

export interface EventSearchStarted {
  search_id: string;
}

/** One summary row of a search; expand it with `getEvent`. */
export interface EventRow {
  event_uid: string;
  /** `YYYY-MM-DD HH:MM:SS.mmm`, UTC. */
  time: string;
  feed: EventFeed;
  dt: string;
  class_uid: number;
  activity_id: number;
  status_id: number;
  severity_id: number;
  source_id: string | null;
  user_uid: string | null;
  user_name: string | null;
  src_ip: string | null;
  device_uid: string | null;
  device_name: string | null;
  app_name: string | null;
  operation: string;
}

export type EventSearchState = 'running' | 'succeeded' | 'failed';

export interface EventSearchPage {
  search_id: string;
  state: EventSearchState;
  items: EventRow[];
  next_cursor: string | null;
  /** More than 1,000 rows matched; only the newest 1,000 are returned. */
  truncated: boolean;
  scanned_bytes: number;
  /** Set when `state` is `failed`. */
  reason: 'scan_cap' | 'timeout' | 'error' | null;
}

/** The full normalized event (the `ocsf` column). */
export interface EventRecord {
  feed: EventFeed;
  dt: string;
  event_uid: string;
  event: Record<string, unknown>;
}

/** The provider's own record, from the raw batch the event came from. */
export interface RawEventRecord {
  feed: EventFeed;
  dt: string;
  event_uid: string;
  raw: Record<string, unknown>;
}

// ---------- BlueSphere rules (Story 31, task 131) ----------

/** One per-Client knob: the catalog's default and bounds, and this Client's value. */
export interface RuleTunable {
  default: number;
  minimum: number;
  maximum: number;
  unit: string;
  value: number;
}

/** A catalog rule with this Client's effective configuration. */
export interface RuleView {
  rule_id: string;
  version: number;
  name: string;
  description: string;
  detection_class: string;
  severity: Severity;
  mitre: string[];
  /** `operations`: written to the Simulint platform-operations tenant, not the Client's. */
  destination: 'client' | 'operations';
  enabled_by_default: boolean;
  enabled: boolean;
  tunables: Record<string, RuleTunable>;
  /** The Client has a stored delta for this rule. */
  customized: boolean;
}

/** `GET /tenants/{t}/rules`. */
export interface RuleList {
  items: RuleView[];
  /** The Routine DLP activity switch: off only when all four routine rules are disabled. */
  routine_dlp: boolean;
}

/** `PUT /tenants/{t}/rules/{rule_id}`: omitted keys return to the catalog's defaults. */
export interface RuleTuningBody {
  enabled?: boolean;
  tunables?: Record<string, number>;
}

/** `PUT /tenants/{t}/settings/routine-dlp`. */
export interface RoutineDlpResult {
  enabled: boolean;
  rules: RuleView[];
}

/** `POST /tenants/{operations}/alerts/{id}/refusals/accept` (task 183): platform Admin only. */
export interface AcceptRefusalsBody {
  client_tenant_id: string;
  source_id: string;
  records: { key: string; reason?: string }[];
}

export interface AcceptRefusalsResult {
  accepted: string[];
  alert_id: string;
  /** The note added to the operations alert's timeline. */
  event: AlertEvent;
}

